import { create, StoreApi, UseBoundStore } from 'zustand';
import { toast } from 'react-toastify';

import getErrorMessage from '../utils/error';
import { CachePaginate, ExtendedPromotionStore, IPromotion } from '../types/types';
import {
  createPromotion,
  deletePromotion,
  getPromotionByServiceId,
  getPromotions,
  updatePromotion,
} from '../utils/api';

const usePromotion: UseBoundStore<StoreApi<ExtendedPromotionStore>> = create<ExtendedPromotionStore>((set, get) => ({
  promotions: [],
  promotionBySrv: [],
  saveLoading: false,
  fetchLoading: false,
  removeLoading: false,
  page: 1,
  pageBySrv: 1,
  hasMore: true,
  hasMoreBySrv: true,
  meta: null,
  metaBySrv: null,
  currentServiceId: null,
  isLoadingNextPage: false,
  cache: {},
  hasFetched: false,
  hasFetchedSrv: false,

  fetchPromotions: async (page = null, reset = false) => {
    try {
      set({ ...(reset ? { fetchLoading: true } : { isLoadingNextPage: true }) });
      const { success, message, data, meta } = await getPromotions(page, page ? 15 : null);
      if (!success) throw new Error(message);

      set((state) => {
        const newData = page
          ? data.filter((item: IPromotion) => !state.promotions.some((p) => p.promo_id === item.promo_id))
          : data;

        // console.log(`Fetching page ${page}, reset: ${reset}, new items: ${newData.length}`);

        let updatedPromotions;
        if (reset) {
          updatedPromotions = data;
        } else if (page) {
          updatedPromotions = [...state.promotions, ...newData];
        } else {
          updatedPromotions = data;
        }

        const isPaginated = meta?.paginated ?? false;
        const totalPages = meta?.totalPages ?? 1;
        const currentPage = page || 1;
        const hasMore = isPaginated ? currentPage < totalPages : false;

        return {
          promotions: updatedPromotions,
          page: currentPage,
          hasMore: data.length > 0 && hasMore,
          meta: meta ?? null,
          hasFetched: true,
        };
      });
      return { message, success, data };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ fetchLoading: false, isLoadingNextPage: false });
    }
  },

  fetchPromotionByServiceId: async (pageBySrv = null, reset = false, serviceId = null) => {
    const currentState = get();
    const cacheKey = `${serviceId}-${pageBySrv || 1}`;

    if (!reset && currentState.cache[cacheKey]) {
      set({
        promotionBySrv: currentState.cache[cacheKey].data,
        pageBySrv: currentState.cache[cacheKey].page,
        hasMoreBySrv: currentState.cache[cacheKey].hasMore,
        meta: currentState.cache[cacheKey].meta,
        currentServiceId: serviceId,
        fetchLoading: false,
        isLoadingNextPage: false,
      });
      return { success: true, message: 'Loaded from cache', data: currentState.cache[cacheKey].data };
    }

    try {
      set({ ...(reset ? { fetchLoading: true } : { isLoadingNextPage: true }) });
      const { success, message, data, meta } = await getPromotionByServiceId(
        pageBySrv,
        pageBySrv ? 10 : null,
        serviceId,
      );
      if (!success) throw new Error(message);

      set((prevState) => {
        const newData = pageBySrv
          ? data.filter((item: IPromotion) => !prevState.promotionBySrv.some((p) => p.promo_id === item.promo_id))
          : data;

        let updatedPromotionBySrv: IPromotion[];
        if (reset) {
          updatedPromotionBySrv = data;
        } else if (pageBySrv) {
          updatedPromotionBySrv = [...prevState.promotionBySrv, ...newData];
        } else {
          updatedPromotionBySrv = data;
        }
        const updatedCache: CachePaginate<IPromotion> = {
          ...prevState.cache,
          [cacheKey]: {
            data: updatedPromotionBySrv,
            page: pageBySrv || 1,
            hasMore: meta.paginated ? (pageBySrv || 1) < (meta.totalPages || 1) : false,
            meta,
          },
        };

        return {
          promotionBySrv: updatedPromotionBySrv,
          pageBySrv: pageBySrv || 1,
          hasMoreBySrv: meta.paginated ? (pageBySrv || 1) < (meta.totalPages || 1) : false,
          meta,
          currentServiceId: serviceId,
          cache: updatedCache,
          hasFetchedSrv: true,
        };
      });
      return { message, success, data };
    } catch (error) {
      const message = getErrorMessage(error);
      return { success: false, message };
    } finally {
      set({ fetchLoading: false, isLoadingNextPage: false });
    }
  },

  addPromotion: async (promotionData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await createPromotion(promotionData);
      if (!success) throw new Error(message);
      set((state) => ({ promotions: state.promotions ? [...state.promotions, data] : [data] }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  editPromotion: async (id, promotionData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await updatePromotion(id, promotionData);
      if (!success) throw new Error(message);
      set((state) => ({
        promotions: state.promotions?.map((promotion) => (promotion.promo_id === id ? data : promotion)),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  removePromotion: async (id) => {
    try {
      set({ removeLoading: true });
      const { success, message } = await deletePromotion(id);
      if (!success) throw new Error(message);
      set((state) => ({
        promotions: state.promotions?.filter((promotion) => promotion.promo_id !== id),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ removeLoading: false });
    }
  },
}));

export default usePromotion;
